import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Upload, RefreshCw, Play, Pause, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { BriefBulletsMidtostenService } from "@/services/briefBullets/briefBulletsMidtostenService";
import { SupabaseStorageService } from "@/services/photo/supabaseStorageService";
import { supabase } from "@/integrations/supabase/client";
import { PromptViewer } from "./PromptViewer";
import { BRIEFR_BULLETS_PROMPTS, SCHEDULE_INFO } from "@/constants/briefBulletsPrompts";

export function BriefBulletsMidtostenManager() {
  const queryClient = useQueryClient();
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const { data: taskStatus, isLoading: taskLoading } = useQuery({
    queryKey: ["briefBulletsMidtostenTask"],
    queryFn: BriefBulletsMidtostenService.getTaskStatus,
    refetchInterval: 30000,
  });

  const { data: weekStatus, isLoading: weekLoading } = useQuery({
    queryKey: ["briefBulletsMidtostenWeek"],
    queryFn: BriefBulletsMidtostenService.getThisWeekStatus,
    refetchInterval: 30000,
  });

  const { data: publishHistory = [] } = useQuery({
    queryKey: ["briefBulletsMidtostenHistory"],
    queryFn: () => BriefBulletsMidtostenService.getPublishHistory(4),
  });

  const { data: taskLogs = [] } = useQuery({
    queryKey: ["briefBulletsMidtostenLogs"],
    queryFn: () => BriefBulletsMidtostenService.getTaskLogs(10),
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (taskStatus?.config?.photo_url) {
      setPhotoUrl(taskStatus.config.photo_url);
    }
  }, [taskStatus]);

  const publishMutation = useMutation({
    mutationFn: BriefBulletsMidtostenService.triggerManualPublish,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Midtøsten-artikkel publisert!");
        queryClient.invalidateQueries({ queryKey: ["briefBulletsMidtostenWeek"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsMidtostenHistory"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsMidtostenLogs"] });
      } else {
        toast.error(result.error || "Feil ved publisering");
      }
    },
    onError: (error: any) => {
      console.error("Error publishing Midtøsten article:", error);
      toast.error("Kunne ikke publisere artikkel");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (enable: boolean) =>
      BriefBulletsMidtostenService.toggleAutomaticPublishing(enable),
    onSuccess: (success, enable) => {
      if (success) {
        toast.success(
          enable
            ? "Automatisk publisering aktivert"
            : "Automatisk publisering deaktivert"
        );
        queryClient.invalidateQueries({ queryKey: ["briefBulletsMidtostenTask"] });
      } else {
        toast.error("Kunne ikke endre status");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: BriefBulletsMidtostenService.deleteThisWeekArticle,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Artikkel slettet");
        queryClient.invalidateQueries({ queryKey: ["briefBulletsMidtostenWeek"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsMidtostenHistory"] });
      } else {
        toast.error(result.error || "Kunne ikke slette artikkel");
      }
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const uploadedUrl = await SupabaseStorageService.uploadImage(
        file,
        "profile-images"
      );
      setPhotoUrl(uploadedUrl);

      const { error } = await supabase.rpc(
        "update_briefr_bullets_midtosten_config",
        {
          new_photo_url: uploadedUrl,
        }
      );

      if (error) throw error;

      toast.success("Bilde lastet opp!");
      queryClient.invalidateQueries({ queryKey: ["briefBulletsMidtostenTask"] });
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Kunne ikke laste opp bilde");
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (taskLoading || weekLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">
          Briefr Bullets: Midtøsten 🏜️
        </h2>
        <p className="text-muted-foreground">
          Publiseres automatisk hver lørdag kl. 05:31
        </p>
      </div>

      {/* Prompt Viewer */}
      <PromptViewer 
        taskName="Midtøsten"
        prompt={BRIEFR_BULLETS_PROMPTS.midtosten}
        config={taskStatus?.config}
        scheduleInfo={SCHEDULE_INFO.midtosten}
      />

      {/* Current Week Status */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-2">Status denne uken</h3>
        {weekStatus?.published ? (
          <div className="space-y-2">
            <p className="text-sm text-green-600 font-medium">
              ✓ Publisert {format(new Date(weekStatus.article!.timestamp), "dd.MM.yyyy HH:mm")}
            </p>
            {weekStatus.article && (
              <div className="mt-3 p-3 bg-muted rounded">
                <p className="text-sm font-medium mb-1">
                  {weekStatus.article.interviewee_name}
                </p>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {weekStatus.article.answer.substring(0, 200)}...
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="mt-3"
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Slett denne uken
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Ingen artikkel publisert denne uken
            </p>
            <Button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="w-full"
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Publiserer...
                </>
              ) : (
                "Publiser nå"
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Automatic Publishing Toggle */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Automatisk publisering</h3>
          <Button
            variant={taskStatus?.status === "active" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              toggleMutation.mutate(taskStatus?.status !== "active")
            }
            disabled={toggleMutation.isPending}
          >
            {taskStatus?.status === "active" ? (
              <>
                <Pause className="h-4 w-4 mr-2" />
                Pause automatisk publisering
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Aktiver automatisk publisering
              </>
            )}
          </Button>
        </div>
        {taskStatus?.next_run && (
          <p className="text-sm text-muted-foreground">
            Neste kjøring:{" "}
            {format(new Date(taskStatus.next_run), "dd.MM.yyyy HH:mm")}
          </p>
        )}
        {taskStatus?.last_run && (
          <p className="text-sm text-muted-foreground">
            Siste kjøring:{" "}
            {format(new Date(taskStatus.last_run), "dd.MM.yyyy HH:mm")}
          </p>
        )}
        {taskStatus?.error_message && (
          <div className="mt-2 p-2 bg-destructive/10 rounded">
            <p className="text-sm text-destructive">
              Feil: {taskStatus.error_message}
            </p>
          </div>
        )}
      </div>

      {/* Profile Photo Upload */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-3">Profilbilde</h3>
        <div className="space-y-3">
          {photoUrl && (
            <div className="relative w-32 h-32 rounded-lg overflow-hidden border">
              <img
                src={photoUrl}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div>
            <Label htmlFor="midtosten-photo-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 border rounded-lg p-3 hover:bg-muted transition-colors">
                <Upload className="h-4 w-4" />
                <span>
                  {uploadingPhoto ? "Laster opp..." : "Last opp nytt bilde"}
                </span>
              </div>
              <Input
                id="midtosten-photo-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploadingPhoto}
              />
            </Label>
          </div>
        </div>
      </div>

      {/* Publish History */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-3">Publiseringshistorikk</h3>
        {publishHistory.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ingen artikler publisert ennå
          </p>
        ) : (
          <div className="space-y-2">
            {publishHistory.map((article) => (
              <div
                key={article.id}
                className="p-3 bg-muted rounded flex justify-between items-center"
              >
                <div>
                  <p className="text-sm font-medium">
                    {article.interviewee_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(article.timestamp), "dd.MM.yyyy HH:mm")}
                  </p>
                </div>
                <span className="text-xs px-2 py-1 bg-background rounded">
                  {article.publication_status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task Logs */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Logger</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: ["briefBulletsMidtostenLogs"],
              })
            }
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        {taskLogs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen logger ennå</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {taskLogs.map((log) => (
              <div
                key={log.id}
                className={`p-2 rounded text-xs ${
                  log.status === "completed"
                    ? "bg-green-50 dark:bg-green-950"
                    : log.status === "failed"
                    ? "bg-red-50 dark:bg-red-950"
                    : "bg-muted"
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium capitalize">{log.status}</span>
                  <span className="text-muted-foreground">
                    {format(new Date(log.created_at), "dd.MM HH:mm")}
                  </span>
                </div>
                {log.message && (
                  <p className="text-muted-foreground">{log.message}</p>
                )}
                {log.execution_time_ms && (
                  <p className="text-muted-foreground mt-1">
                    Tid: {(log.execution_time_ms / 1000).toFixed(2)}s
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
